import { Code, Loader, Stack, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { Select } from 'src/design-system/Select';
import { SectionDraft } from './types';

type SectionDraftsModalContentProps = {
  drafts: SectionDraft[];
};

type SectionDraftsModalBodyProps = {
  drafts: SectionDraft[] | undefined;
  isLoading: boolean;
  isError: boolean;
  adviceJourneyInput?: string | null;
};

function MonoRow({ label, value }: { label: string; value: string | null | undefined }) {
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;
  return (
    <Text size="sm" style={{ fontFamily: 'monospace' }}>
      <Text span fw={600}>
        {label}:
      </Text>{' '}
      {displayValue}
    </Text>
  );
}

function DraftPanel({ draft }: { draft: SectionDraft }) {
  return (
    <Stack gap="md">
      <Stack gap="xs">
        <MonoRow label="Display order" value={String(draft.displayOrder)} />
        <MonoRow label="Heading" value={draft.heading} />
        <MonoRow label="Model" value={draft.llmModelName} />
        <MonoRow label="Finish reason" value={draft.llmFinishReason} />
        <MonoRow label="Created at" value={draft.createdAt} />
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Drafted content</Title>
        <Code
          block
          style={{
            maxHeight: '40vh',
            maxWidth: '100%',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {draft.draftedContent || 'Empty content'}
        </Code>
      </Stack>

      <Stack gap="xs">
        <Title order={5}>Reasoning</Title>
        {draft.llmReasoningContent ? (
          <Code
            block
            style={{
              maxHeight: '40vh',
              maxWidth: '100%',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {draft.llmReasoningContent}
          </Code>
        ) : (
          <Text c="dimmed" size="sm">
            No reasoning captured (non-thinking model)
          </Text>
        )}
      </Stack>
    </Stack>
  );
}

function AdviceJourneyInputPanel({ content }: { content: string }) {
  return (
    <Stack gap="xs">
      <Title order={5}>Advice journey input (woven into every section)</Title>
      <Code
        block
        style={{
          maxHeight: '40vh',
          maxWidth: '100%',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {content}
      </Code>
    </Stack>
  );
}

export function SectionDraftsModalBody({
  drafts,
  isLoading,
  isError,
  adviceJourneyInput,
}: SectionDraftsModalBodyProps) {
  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return <Text c="red">Failed to load section drafts.</Text>;
  }
  if (!drafts) {
    return null;
  }
  return (
    <Stack gap="lg">
      {adviceJourneyInput ? <AdviceJourneyInputPanel content={adviceJourneyInput} /> : null}
      <SectionDraftsModalContent drafts={drafts} />
    </Stack>
  );
}

function sectionLabel(draft: SectionDraft): string {
  return draft.heading && draft.heading.trim().length > 0
    ? draft.heading.replace(/^#+\s*/, '')
    : `Section ${draft.displayOrder + 1}`;
}

function SectionDraftsModalContent({ drafts }: SectionDraftsModalContentProps) {
  const ordered = [...drafts].sort((a, b) => a.displayOrder - b.displayOrder);
  const [selected, setSelected] = useState<string | null>(ordered[0]?.id ?? null);

  if (ordered.length === 0) {
    return <Text c="dimmed">No drafts captured for this document.</Text>;
  }

  const activeDraft = ordered.find(draft => draft.id === selected) ?? ordered[0];

  return (
    <Stack gap="md">
      <Select
        label="Section"
        data={ordered.map(draft => ({ value: draft.id, label: sectionLabel(draft) }))}
        value={activeDraft.id}
        onChange={value => {
          if (value) setSelected(value);
        }}
        allowDeselect={false}
        searchable
        maxDropdownHeight={320}
        style={{ maxWidth: 360 }}
      />
      <DraftPanel draft={activeDraft} />
    </Stack>
  );
}
